import { supabase } from '@/integrations/supabase/client';
import { 
  getTaskListTemplates, 
  getTaskTemplates, 
  getEventTemplates,
  getPersonTemplates,
  getDocumentTemplates,
  type FamilyMember 
} from './demoContentTemplates';

interface GenerateDemoContentOptions {
  familyId: string;
  familyMembers: FamilyMember[];
  familyName: string;
  currentProfileId: string;
}

export async function generateDemoContent({
  familyId,
  familyMembers,
  familyName,
  currentProfileId
}: GenerateDemoContentOptions): Promise<void> {
  console.log('🎭 Starting demo content generation...');
  
  const hasChildren = familyMembers.some(m => m.role === 'child' || m.role === 'son' || m.role === 'daughter');
  const parents = familyMembers.filter(m => m.role === 'parent' || m.role === 'father' || m.role === 'mother' || m.role === 'spouse');
  const children = familyMembers.filter(m => m.role === 'child' || m.role === 'son' || m.role === 'daughter');
  const familyLastName = familyMembers[0]?.lastName || familyName.split(' ')[0] || 'Dupont';
  
  try {
    // Generate task lists first (needed for tasks)
    console.log('📝 Generating task lists...');
    await generateTaskLists(familyId, currentProfileId, hasChildren);
    
    // Generate events and get the welcome event ID
    console.log('📅 Generating events...');
    const events = await generateEvents(familyId, currentProfileId, hasChildren, children);
    const welcomeEvent = events?.find(e => e.title === 'Bienvenue dans votre Clan');
    console.log('🎉 Welcome event:', welcomeEvent ? `Found with ID ${welcomeEvent.id}` : 'NOT FOUND');
    
    // Generate tasks with welcome event ID
    console.log('✅ Generating tasks...');
    await generateTasks(familyId, currentProfileId, hasChildren, parents, children, welcomeEvent?.id);
    
    // Generate persons and documents in parallel
    console.log('👥 Generating persons and documents...');
    await Promise.all([
      generatePersons(familyId, currentProfileId, familyLastName, hasChildren),
      generateDocuments(familyId, currentProfileId, hasChildren),
    ]);
    
    console.log('✅ Demo content generation complete!');
  } catch (error) {
    console.error('❌ Error generating demo content:', error);
    throw error;
  }
}

async function generateTaskLists(
  familyId: string,
  currentProfileId: string,
  hasChildren: boolean
): Promise<void> {
  const lists = getTaskListTemplates(hasChildren);
  
  const listsData = lists.map((list, index) => ({
    family_id: familyId,
    created_by: currentProfileId,
    name: list.name,
    icon: list.icon,
    display_order: index,
  }));
  
  const { error } = await supabase
    .from('task_lists')
    .insert(listsData);
  
  if (error) throw error;
  console.log(`✅ Created ${lists.length} task lists`);
}

async function generateTasks(
  familyId: string,
  currentProfileId: string,
  hasChildren: boolean,
  parents: FamilyMember[],
  children: FamilyMember[],
  welcomeEventId?: string
): Promise<void> {
  // Add welcome tasks first if we have a welcome event
  const welcomeTasks = welcomeEventId ? [
    {
      family_id: familyId,
      created_by: currentProfileId,
      assigned_to: currentProfileId,
      assigned_to_list: [currentProfileId],
      title: "Découvrir l'app",
      description: "Explorer les fonctionnalités de votre clan",
      due_date: new Date().toISOString().split('T')[0],
      status: 'pending' as const,
      event_id: welcomeEventId,
      display_order: 0,
    },
    {
      family_id: familyId,
      created_by: currentProfileId,
      assigned_to: currentProfileId,
      assigned_to_list: [currentProfileId],
      title: "Inviter les membres de ma famille",
      description: "Partager votre clan avec vos proches",
      due_date: new Date().toISOString().split('T')[0],
      status: 'pending' as const,
      event_id: welcomeEventId,
      display_order: 1,
    }
  ] : [];
  
  const taskTemplates = getTaskTemplates(hasChildren);
  
  // Fetch the created lists to get their IDs
  const { data: lists, error: listsError } = await supabase
    .from('task_lists')
    .select('id, name')
    .eq('family_id', familyId);
  
  if (listsError) throw listsError;
  
  const listMap = new Map(lists?.map(l => [l.name, l.id]) || []);
  
  const tasksData = taskTemplates.map((template, index) => {
    let assignedTo = currentProfileId;
    
    if (template.assignedToRole === 'parent' && parents.length > 0) {
      assignedTo = parents[Math.floor(Math.random() * parents.length)].id;
    } else if (template.assignedToRole === 'child' && children.length > 0) {
      assignedTo = children[Math.floor(Math.random() * children.length)].id;
    }
    
    const dueDate = new Date();
    dueDate.setDate(dueDate.getDate() + template.daysFromNow);
    
    return {
      family_id: familyId,
      created_by: currentProfileId,
      assigned_to: assignedTo,
      assigned_to_list: [assignedTo],
      title: template.title,
      description: template.description,
      due_date: dueDate.toISOString().split('T')[0],
      status: 'pending' as const,
      list_id: listMap.get(template.listName) || null,
      display_order: index,
    };
  });
  
  const allTasks = [...welcomeTasks, ...tasksData];
  
  console.log(`🔨 Inserting ${allTasks.length} tasks (${welcomeTasks.length} welcome tasks, ${tasksData.length} regular tasks)...`);
  
  const { data: insertedTasks, error } = await supabase
    .from('family_tasks')
    .insert(allTasks)
    .select();
  
  if (error) {
    console.error('❌ Error inserting tasks:', error);
    throw error;
  }
  console.log(`✅ Created ${insertedTasks?.length || 0} tasks successfully`);
}

async function generateEvents(
  familyId: string,
  currentProfileId: string,
  hasChildren: boolean,
  children: FamilyMember[]
): Promise<any[] | null> {
  const eventTemplates = getEventTemplates(hasChildren);
  
  const eventsData = eventTemplates
    .filter(template => !template.requiresChildren || hasChildren)
    .map(template => {
      const startDate = new Date();
      startDate.setDate(startDate.getDate() + template.daysFromNow);
      startDate.setHours(template.type === 'appointment' ? 14 : 18, 0, 0, 0);
      
      const endDate = new Date(startDate);
      endDate.setHours(endDate.getHours() + template.duration);
      
      return {
        family_id: familyId,
        created_by: currentProfileId,
        title: template.title,
        description: template.description,
        start_date: startDate.toISOString(),
        end_date: endDate.toISOString(),
        event_type: template.type,
        is_recurring: false,
      };
    });
  
  // Add birthday events for children if they have birth dates
  for (const child of children) {
    if (child.dateOfBirth) {
      const birthDate = new Date(child.dateOfBirth);
      const thisYear = new Date().getFullYear();
      const nextBirthday = new Date(thisYear, birthDate.getMonth(), birthDate.getDate());
      
      if (nextBirthday < new Date()) {
        nextBirthday.setFullYear(thisYear + 1);
      }
      
      const age = nextBirthday.getFullYear() - birthDate.getFullYear();
      
      eventsData.push({
        family_id: familyId,
        created_by: currentProfileId,
        title: `🎂 Anniversaire de ${child.firstName}`,
        description: `${child.firstName} aura ${age} ans !`,
        start_date: nextBirthday.toISOString(),
        end_date: new Date(nextBirthday.getTime() + 3600000).toISOString(),
        event_type: 'family' as const, // Use 'family' instead of 'birthday' for now
        is_recurring: true,
      });
    }
  }
  
  const { data: events, error } = await supabase
    .from('family_events')
    .insert(eventsData)
    .select();
  
  if (error) throw error;
  console.log(`✅ Created ${eventsData.length} events`);
  return events;
}

async function generatePersons(
  familyId: string,
  currentProfileId: string,
  familyLastName: string,
  hasChildren: boolean
): Promise<void> {
  const personTemplates = getPersonTemplates(familyLastName, hasChildren);
  
  const personsData = personTemplates.map(template => ({
    family_id: familyId,
    created_by: currentProfileId,
    first_name: template.firstName,
    last_name: template.lastName,
    category: template.category as any,
    phone_primary: template.phone,
    email: template.email,
    emergency_contact: template.category === 'medical_other',
  }));
  
  const { error } = await supabase
    .from('person_profiles')
    .insert(personsData);
  
  if (error) throw error;
  console.log(`✅ Created ${personsData.length} person profiles`);
}

async function generateDocuments(
  familyId: string,
  currentProfileId: string,
  hasChildren: boolean
): Promise<void> {
  const documentTemplates = getDocumentTemplates(hasChildren);
  
  const documentsData = documentTemplates
    .filter(template => !template.requiresChildren || hasChildren)
    .map(template => ({
      family_id: familyId,
      created_by: currentProfileId,
      title: template.title,
      content: template.content,
      category: template.category,
      document_type: 'note',
    }));
  
  console.log(`📄 Inserting ${documentsData.length} documents...`);
  
  const { data: insertedDocs, error } = await supabase
    .from('family_documents')
    .insert(documentsData)
    .select();
  
  if (error) {
    console.error('❌ Error inserting documents:', error);
    throw error;
  }
  console.log(`✅ Created ${insertedDocs?.length || 0} documents successfully`);
}
